import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import DashboardHeader from "@/components/DashboardHeader";
import BackButton from "@/components/BackButton";
import CheckoutDialog from "@/components/CheckoutDialog";
import ReferralBanner from "@/components/ReferralBanner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { coverBg, formatPrice, getPublicCourse } from "@/lib/courses";
import { openBrochurePdf } from "@/lib/payments";
import {
  clearPendingReferralToken,
  readPendingReferralToken,
} from "@/lib/referrals";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { BookOpen, FileText, Loader2, Lock, ShoppingCart } from "lucide-react";

export default function PublicCourseDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const referralToken = searchParams.get("ref");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [err, setErr] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [checkoutState, setCheckoutState] = useState({ open: false, enrollment: null });

  useEffect(() => {
    getPublicCourse(id)
      .then(setCourse)
      .catch((e) => setErr(e?.response?.data?.detail || "Not available"));
  }, [id]);

  const canSelfEnroll =
    user &&
    user.role === "student" &&
    user.status === "approved";

  const startSelfEnroll = async () => {
    if (!user) {
      navigate(`/login?redirect=/courses/${id}${referralToken ? `?ref=${referralToken}` : ""}`);
      return;
    }
    if (user.role !== "student" || user.status !== "approved") {
      toast.error("Only approved students can enroll");
      return;
    }
    setEnrolling(true);
    try {
      // Attach any pending referral token stored for this course (either
      // set now by the banner or persisted from a previous visit).
      const stored = readPendingReferralToken(id);
      const body = { course_id: id };
      if (referralToken || stored) {
        body.referral_token = referralToken || stored;
      }
      const { data } = await api.post("/student/enrollments/self-enroll", body);
      // Clear the pending token — it's now attached to the enrollment.
      clearPendingReferralToken();
      setCheckoutState({ open: true, enrollment: data });
    } catch (e) {
      const d = e?.response?.data?.detail || "Enrollment failed";
      if (d === "already_enrolled") {
        toast.error("You're already enrolled in this course");
        navigate(`/student/courses/${id}`);
      } else if (d === "self_referral_not_allowed") {
        toast.error("You can't use your own referral link — sharing it works though!");
        clearPendingReferralToken();
      } else if (typeof d === "string" && d.startsWith("referral_")) {
        toast.error("This invite link is no longer valid. Enrolling without it.");
        clearPendingReferralToken();
      } else {
        toast.error(typeof d === "string" ? d : "Enrollment failed");
      }
    } finally {
      setEnrolling(false);
    }
  };

  const onPaymentComplete = () => {
    setCheckoutState({ open: false, enrollment: null });
    navigate(`/student/courses/${id}`);
  };

  if (err) {
    return (
      <div className="min-h-screen bg-stone-50">
        <DashboardHeader subtitle="Course detail" />
        <div className="mx-auto max-w-3xl px-6 py-16 text-center text-stone-600">
          {err}
        </div>
      </div>
    );
  }
  if (!course) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const plans = course.pricing.plans;

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="public-course-detail">
      <DashboardHeader subtitle="Course detail" />
      <main className="mx-auto max-w-4xl px-6 py-8 space-y-6">
        <BackButton to="/courses" label="Back to catalog" />
        {referralToken && <ReferralBanner courseId={id} token={referralToken} />}
        <div className={`h-32 rounded-md ${coverBg(course.cover_color)} border`} />
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight" data-testid="course-title">
              {course.title}
            </h1>
            {course.subtitle && (
              <p className="mt-1 text-stone-600">{course.subtitle}</p>
            )}
            <div className="mt-2 flex gap-2">
              <Badge variant="outline">{course.level}</Badge>
              <Badge variant="outline">{course.chapter_titles.length} chapters</Badge>
            </div>
          </div>
          <Card className="border-stone-200 min-w-[260px]" data-testid="pricing-summary">
            <CardContent className="py-4 space-y-2">
              <div className="text-2xl font-semibold" data-testid="price-display">
                {formatPrice(course.pricing)}
              </div>
              {plans.pay_in_full.enabled && (
                <div className="text-xs text-stone-600" data-testid="plan-pay-summary">
                  Pay in full
                  {plans.pay_in_full.discount_percent > 0
                    ? ` (save ${plans.pay_in_full.discount_percent}%)`
                    : ""}
                </div>
              )}
              {plans.part_payment.enabled && (
                <div className="text-xs text-stone-600" data-testid="plan-part-summary">
                  Pay in {plans.part_payment.num_parts} parts
                </div>
              )}
              {plans.monthly.enabled && (
                <div className="text-xs text-stone-600" data-testid="plan-monthly-summary">
                  {plans.monthly.num_months}-month subscription
                </div>
              )}
              {canSelfEnroll ? (
                <Button
                  onClick={startSelfEnroll}
                  disabled={enrolling}
                  className="w-full mt-2 bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="enroll-and-pay-button"
                >
                  {enrolling ? (
                    <>
                      <Loader2 className="mr-1 h-4 w-4 animate-spin" /> Starting…
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="mr-1 h-4 w-4" /> Enroll & Pay
                    </>
                  )}
                </Button>
              ) : !user ? (
                <Button
                  onClick={() => navigate(`/login?redirect=/courses/${id}${referralToken ? `?ref=${referralToken}` : ""}`)}
                  className="w-full mt-2 bg-stone-900 hover:bg-stone-800 text-white"
                  data-testid="enroll-signin-cta"
                >
                  {referralToken ? "Sign in to accept invite" : "Sign in to enroll"}
                </Button>
              ) : (
                <Button
                  disabled
                  className="w-full mt-2 bg-stone-900 text-white opacity-50"
                  data-testid="enrol-button-disabled"
                >
                  <Lock className="mr-1 h-4 w-4" /> Enrolment for students only
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full"
                onClick={() =>
                  openBrochurePdf(course.id, `KKS_${course.title}_brochure.pdf`).catch(
                    (e) => toast.error(e?.message || "Failed to download brochure")
                  )
                }
                data-testid="download-brochure-button"
              >
                <FileText className="mr-1 h-4 w-4" /> Download brochure
              </Button>
            </CardContent>
          </Card>
        </div>

        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="text-base">About this course</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap text-stone-700">
              {course.description || "No description provided."}
            </p>
          </CardContent>
        </Card>

        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-stone-500" /> Chapters
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {course.chapter_titles.map((ch, i) => (
              <div
                key={ch.id}
                className="px-4 py-3 border-b border-stone-100 last:border-b-0"
                data-testid={`chapter-summary-${i}`}
              >
                <span className="text-stone-400 mr-2">{i + 1}.</span>
                {ch.title}
              </div>
            ))}
          </CardContent>
        </Card>
      </main>
      <CheckoutDialog
        open={checkoutState.open}
        enrollment={checkoutState.enrollment}
        course={course}
        onClose={() => setCheckoutState({ open: false, enrollment: null })}
        onPaymentComplete={onPaymentComplete}
      />
    </div>
  );
}
